import { useEffect, useMemo, useState } from "react";
import {
  CalendarBlank,
  Plus,
  PencilSimple,
  Trash,
  X,
  CaretLeft,
  CaretRight,
  Clock,
  CheckCircle,
  ArrowCounterClockwise,
  SlidersHorizontal,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StaffShiftTemplateWorkspace from "@/components/admin/StaffShiftTemplateWorkspace";
import {
  listShifts,
  createShift,
  updateShift,
  deleteShift,
  getStaffWeekSummary,
  listAdminEntriesForWeek,
  getClassroomRatioByDay,
  startOfWeek,
  formatDuration,
  computeEntryMinutes,
  getStaffTimecardReviewSummary,
  getStaffTimecardSnapshot,
  approveStaffTimecard,
  reopenStaffTimecard,
  correctStaffTimesheetEntry,
  updateStaffTimekeepingPolicy,
  localDateKey,
  timecardStatusLabel,
} from "@/lib/scheduling";

/**
 * Admin · /admin/scheduling
 *
 * Weekly grid of shifts (staff × days) + a compact week-summary strip
 * comparing scheduled vs worked minutes per staff. Click any cell to
 * add a shift; click a shift chip to edit/delete.
 */
export default function AdminScheduling() {
  const { profile } = useAuth();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [staff, setStaff] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [summary, setSummary] = useState([]);
  const [entries, setEntries] = useState([]);
  const [ratios, setRatios] = useState([]);
  const [timecardReview, setTimecardReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | { shift?, profileId, day }
  const [timecardEditor, setTimecardEditor] = useState(null); // null | { profileId, fullName }
  const [policyEditor, setPolicyEditor] = useState(false);
  const [tab, setTab] = useState("grid"); // 'grid' | 'templates' | 'timesheets'

  const weekEnd = useMemo(() => {
    const e = new Date(weekStart);
    e.setDate(e.getDate() + 7);
    return e;
  }, [weekStart]);

  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [weekStart]);

  async function refresh() {
    if (!profile?.organization_id) {
      setStaff([]);
      setClassrooms([]);
      setShifts([]);
      setSummary([]);
      setEntries([]);
      setRatios([]);
      setTimecardReview(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const orgId = profile.organization_id;
      // Fetch profile ids that have the teacher role in this org, then
      // expand into full profiles. Avoids PostgREST's inner-join filter
      // quirks and gives us clean dedup.
      const { data: ra, error: raErr } = await supabase
        .from("role_assignments")
        .select("profile_id")
        .eq("role", "teacher")
        .eq("organization_id", orgId);
      if (raErr) throw raErr;
      const teacherIds = Array.from(new Set((ra || []).map((r) => r.profile_id)));
      let stData = [];
      if (teacherIds.length > 0) {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", teacherIds)
          .order("full_name");
        if (error) throw error;
        stData = data || [];
      }

      const { data: cl, error: clErr } = await supabase
        .from("classrooms")
        .select("id, name")
        .eq("organization_id", orgId)
        .order("name");
      if (clErr) throw clErr;

      const [sh, sum, en, rat, review] = await Promise.all([
        listShifts({
          organizationId: orgId,
          from: weekStart.toISOString(),
          to: weekEnd.toISOString(),
        }),
        getStaffWeekSummary({
          weekStart: weekStart.toISOString(),
          weekEnd: weekEnd.toISOString(),
        }),
        listAdminEntriesForWeek({
          weekStart: weekStart.toISOString(),
          weekEnd: weekEnd.toISOString(),
        }),
        getClassroomRatioByDay({
          weekStart: weekStart.toISOString(),
          weekEnd: weekEnd.toISOString(),
        }).catch(() => []),
        // Keep the established schedule screen available during a rolling
        // database deployment; the review panel appears as soon as its RPC is
        // present for this environment.
        getStaffTimecardReviewSummary({ weekStart: localDateKey(weekStart) }).catch(() => null),
      ]);

      // Dedupe staff rows.
      const byId = {};
      for (const s of stData) byId[s.id] = { id: s.id, full_name: s.full_name, email: s.email };
      setStaff(Object.values(byId));
      setClassrooms(cl || []);
      setShifts(sh);
      setSummary(sum);
      setEntries(en);
      setRatios(rat || []);
      setTimecardReview(review);
    } catch (err) {
      toast.error(err?.message || "Could not load schedule");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id, weekStart]);

  // Group shifts by (profileId, dayISO).
  const shiftsByCell = useMemo(() => {
    const m = {};
    for (const s of shifts) {
      const d = new Date(s.starts_at);
      const key = `${s.profile_id}::${d.toDateString()}`;
      (m[key] ||= []).push(s);
    }
    return m;
  }, [shifts]);

  const summaryById = useMemo(
    () => Object.fromEntries(summary.map((s) => [s.profile_id, s])),
    [summary],
  );

  function shiftWeek(delta) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await deleteShift(id);
      toast.success("Shift deleted");
      refresh();
    } catch (err) {
      toast.error(err?.message || "Could not delete");
    }
  }

  return (
    <div data-testid="admin-scheduling" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Operations
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900">
            Scheduling & timesheets
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Plan shifts for the week and track what actually got worked.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="admin-scheduling-prev-week"
            onClick={() => shiftWeek(-1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white hover:bg-neutral-50"
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <span
            data-testid="admin-scheduling-week-label"
            className="rounded-full bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-800"
          >
            Week of {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          </span>
          <button
            type="button"
            data-testid="admin-scheduling-next-week"
            onClick={() => shiftWeek(1)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-white hover:bg-neutral-50"
          >
            <CaretRight size={14} weight="bold" />
          </button>
        </div>
      </header>

      {/* Tab switcher */}
      <div
        data-testid="admin-scheduling-tabs"
        role="tablist"
        aria-label="Scheduling workspace"
        className="inline-flex rounded-full border border-neutral-200 bg-white p-0.5"
      >
        <button
          type="button"
          id="admin-scheduling-tab-grid"
          role="tab"
          aria-selected={tab === "grid"}
          aria-controls="admin-scheduling-panel"
          data-testid="admin-scheduling-tab-grid"
          data-active={tab === "grid"}
          onClick={() => setTab("grid")}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ${
            tab === "grid" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          <CalendarBlank size={14} weight="duotone" />
          Schedule
        </button>
        <button
          type="button"
          id="admin-scheduling-tab-templates"
          role="tab"
          aria-selected={tab === "templates"}
          aria-controls="admin-scheduling-panel"
          data-testid="admin-scheduling-tab-templates"
          data-active={tab === "templates"}
          onClick={() => setTab("templates")}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ${
            tab === "templates" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          <CalendarBlank size={14} weight="duotone" />
          Templates
        </button>
        <button
          type="button"
          id="admin-scheduling-tab-timesheets"
          role="tab"
          aria-selected={tab === "timesheets"}
          aria-controls="admin-scheduling-panel"
          data-testid="admin-scheduling-tab-timesheets"
          data-active={tab === "timesheets"}
          onClick={() => setTab("timesheets")}
          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold ${
            tab === "timesheets" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"
          }`}
        >
          <Clock size={14} weight="duotone" />
          Timesheets
        </button>
      </div>

      <div id="admin-scheduling-panel" role="tabpanel" aria-labelledby={`admin-scheduling-tab-${tab}`}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          </div>
        ) : tab === "grid" ? (
          <>
            <ClassroomRatioStrip ratios={ratios} days={days} />
            <SchedulingGrid
              staff={staff}
              days={days}
              shiftsByCell={shiftsByCell}
              summaryById={summaryById}
              classrooms={classrooms}
              onAdd={(profileId, day) => setEditor({ profileId, day })}
              onEdit={(shift) => setEditor({ shift, profileId: shift.profile_id, day: new Date(shift.starts_at) })}
              onDelete={handleDelete}
            />
          </>
        ) : tab === "templates" ? (
          <StaffShiftTemplateWorkspace
            organizationId={profile?.organization_id}
            staff={staff}
            classrooms={classrooms}
            weekStart={weekStart}
            onApplied={refresh}
          />
        ) : (
          <TimesheetsTab
            summary={summary}
            entries={entries}
            timecardReview={timecardReview}
            onReview={(row) => setTimecardEditor({ profileId: row.profileId, fullName: row.fullName })}
            onConfigurePolicy={() => setPolicyEditor(true)}
          />
        )}
      </div>

      {editor && (
        <ShiftEditor
          shift={editor.shift}
          profileId={editor.profileId}
          day={editor.day}
          staff={staff}
          classrooms={classrooms}
          organizationId={profile?.organization_id}
          createdBy={profile?.id}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            refresh();
          }}
        />
      )}

      {timecardEditor && (
        <TimecardReviewDialog
          profileId={timecardEditor.profileId}
          fullName={timecardEditor.fullName}
          weekStart={localDateKey(weekStart)}
          onClose={() => setTimecardEditor(null)}
          onChanged={refresh}
        />
      )}

      {policyEditor && (
        <TimekeepingPolicyDialog
          policy={timecardReview?.policy}
          onClose={() => setPolicyEditor(false)}
          onSaved={async () => {
            setPolicyEditor(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function SchedulingGrid({ staff, days, shiftsByCell, summaryById, onAdd, onEdit, onDelete }) {
  if (staff.length === 0) {
    return (
      <p
        data-testid="admin-scheduling-empty"
        className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-8 text-center text-sm text-neutral-500"
      >
        No staff with the teacher role yet. Add them under Staff first.
      </p>
    );
  }

  return (
    <div data-testid="admin-scheduling-grid" className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
      <table className="min-w-[780px] w-full">
        <thead className="bg-neutral-50">
          <tr>
            <th className="sticky left-0 z-10 bg-neutral-50 p-3 text-left text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              Staff
            </th>
            {days.map((d) => (
              <th
                key={d.toISOString()}
                className="p-3 text-center text-[11px] font-bold uppercase tracking-wider text-neutral-500"
              >
                {d.toLocaleDateString(undefined, { weekday: "short" })}
                <br />
                <span className="font-semibold text-neutral-800">
                  {d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </span>
              </th>
            ))}
            <th className="p-3 text-center text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              Scheduled / Worked
            </th>
          </tr>
        </thead>
        <tbody>
          {staff.map((p) => (
            <tr key={p.id} className="border-t border-neutral-100">
              <td className="sticky left-0 z-10 bg-white p-3 align-top">
                <p className="font-heading text-sm font-semibold text-neutral-900">{p.full_name}</p>
                <p className="text-[11px] text-neutral-500">{p.email}</p>
              </td>
              {days.map((d) => {
                const key = `${p.id}::${d.toDateString()}`;
                const list = shiftsByCell[key] || [];
                return (
                  <td
                    key={key}
                    data-testid={`admin-scheduling-cell-${p.id}-${d.toISOString().slice(0, 10)}`}
                    className="min-w-[96px] align-top p-2"
                  >
                    <div className="flex flex-col gap-1">
                      {list.map((s) => (
                        <div
                          key={s.id}
                          data-testid={`admin-scheduling-shift-${s.id}`}
                          className="group flex items-center justify-between gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2 py-1"
                        >
                          <button
                            type="button"
                            onClick={() => onEdit(s)}
                            data-testid={`admin-scheduling-shift-${s.id}-edit`}
                            className="min-w-0 flex-1 text-left"
                          >
                            <p className="truncate text-[11px] font-semibold text-primary-hover">
                              {new Date(s.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                              {" – "}
                              {new Date(s.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                            </p>
                            {s.role_label && (
                              <p className="truncate text-[10px] uppercase tracking-wider text-primary-hover/80">
                                {s.role_label}
                              </p>
                            )}
                          </button>
                          <button
                            type="button"
                            data-testid={`admin-scheduling-shift-${s.id}-delete`}
                            onClick={() => onDelete(s.id)}
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-neutral-400 opacity-0 group-hover:opacity-100 hover:bg-[#C6851E]/10 hover:text-[#C6851E]"
                            aria-label="Delete shift"
                          >
                            <Trash size={10} weight="bold" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        data-testid={`admin-scheduling-add-${p.id}-${d.toISOString().slice(0, 10)}`}
                        onClick={() => onAdd(p.id, d)}
                        className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-neutral-200 bg-neutral-50/40 p-1 text-[10px] font-semibold text-neutral-500 hover:border-primary/40 hover:text-primary-hover"
                      >
                        <Plus size={10} weight="bold" />
                        Shift
                      </button>
                    </div>
                  </td>
                );
              })}
              <td className="p-3 align-top">
                <p
                  data-testid={`admin-scheduling-summary-${p.id}`}
                  className="whitespace-nowrap text-center text-xs"
                >
                  <span className="font-semibold text-neutral-900">
                    {formatDuration(summaryById[p.id]?.scheduled_minutes || 0)}
                  </span>
                  {" / "}
                  <span className={summaryById[p.id]?.worked_minutes ? "font-semibold text-primary-hover" : "text-neutral-500"}>
                    {formatDuration(summaryById[p.id]?.worked_minutes || 0)}
                  </span>
                  {summaryById[p.id]?.open_entry && (
                    <span className="ml-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-hover">
                      In
                    </span>
                  )}
                </p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TimesheetsTab({ summary, entries, timecardReview, onReview, onConfigurePolicy }) {
  return (
    <div className="space-y-4">
      <section
        data-testid="admin-timesheets-week-summary"
        className="rounded-2xl border border-neutral-200 bg-white p-4"
      >
        <h2 className="mb-3 font-heading text-base font-semibold text-neutral-900">
          This week
        </h2>
        {summary.length === 0 ? (
          <p className="text-sm text-neutral-500">No staff to summarise.</p>
        ) : (
          <ul className="space-y-2">
            {summary.map((s) => {
              const pct =
                s.scheduled_minutes > 0
                  ? Math.min(100, Math.round((s.worked_minutes / s.scheduled_minutes) * 100))
                  : 0;
              return (
                <li
                  key={s.profile_id}
                  data-testid={`admin-timesheets-summary-row-${s.profile_id}`}
                  className="flex items-center gap-3"
                >
                  <div className="min-w-[140px]">
                    <p className="text-sm font-medium text-neutral-900">{s.full_name}</p>
                  </div>
                  <div className="flex-1">
                    <div className="h-2 overflow-hidden rounded-full bg-neutral-100">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                  <p className="min-w-[140px] text-right text-xs text-neutral-600">
                    <span className="font-semibold text-neutral-900">
                      {formatDuration(s.worked_minutes)}
                    </span>
                    {" / "}
                    {formatDuration(s.scheduled_minutes)}
                    {s.open_entry && (
                      <span className="ml-1.5 rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-hover">
                        In
                      </span>
                    )}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <TimecardReviewPanel
        review={timecardReview}
        onReview={onReview}
        onConfigurePolicy={onConfigurePolicy}
      />

      <section data-testid="admin-timesheets-entries">
        <h2 className="mb-3 font-heading text-base font-semibold text-neutral-900">
          All entries
        </h2>
        {entries.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
            No time entries this week.
          </p>
        ) : (
          <ul className="space-y-2">
            {entries.map((e) => {
              const mins = computeEntryMinutes(e, new Date());
              return (
                <li
                  key={e.id}
                  data-testid={`admin-timesheet-entry-${e.id}`}
                  data-open={!e.clock_out}
                  className="flex items-start justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
                >
                  <div className="min-w-0">
                    <p className="font-heading text-sm font-semibold text-neutral-900">
                      {e.profiles?.full_name || "—"}
                      {!e.clock_out && (
                        <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover">
                          Clocked in
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {new Date(e.clock_in).toLocaleString()} →{" "}
                      {e.clock_out ? new Date(e.clock_out).toLocaleString() : "—"}
                      {e.classrooms?.name && <span> · {e.classrooms.name}</span>}
                      {e.break_minutes > 0 && <span> · {e.break_minutes}m break</span>}
                    </p>
                    {e.notes && (
                      <p className="mt-1 text-xs text-neutral-600">"{e.notes}"</p>
                    )}
                  </div>
                  <p className="shrink-0 font-heading text-sm font-semibold text-neutral-900">
                    {formatDuration(mins)}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function TimecardReviewPanel({ review, onReview, onConfigurePolicy }) {
  if (!review) {
    return (
      <section
        data-testid="admin-timecard-review-unavailable"
        data-state="unavailable"
        className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 p-4"
      >
        <p className="text-sm font-medium text-neutral-700">Timecard review will be available when this center’s update is activated.</p>
        <p className="mt-1 text-xs text-neutral-500">
          The existing scheduling and clock views remain available while the review workspace is activated.
        </p>
      </section>
    );
  }

  const rows = review.timecards || [];
  const thresholdConfigured = review.policy?.status === "configured";

  return (
    <section
      data-testid="admin-timecard-review"
      className="rounded-2xl border border-neutral-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-neutral-900">Timecard review</h2>
          <p className="mt-1 text-xs text-neutral-500">
            Review actual hours, corrections, and manager approvals. Payroll remains outside HoneyAcorn.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-timecard-configure-policy"
          onClick={onConfigurePolicy}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <SlidersHorizontal size={14} weight="duotone" />
          Configure review threshold
        </button>
      </div>

      <div className={`mt-3 rounded-xl px-3 py-2 text-xs ${
        thresholdConfigured ? "bg-primary/5 text-primary-hover" : "bg-neutral-50 text-neutral-600"
      }`}>
        {thresholdConfigured
          ? `Center review threshold: ${formatDuration(review.policy.weeklyReviewThresholdMinutes)} per Monday-start week. Time above it is a review signal, not a payroll calculation.`
          : "No weekly review threshold is configured. HoneyAcorn will show actual time only until your center sets one."}
      </div>

      {rows.length === 0 ? (
        <p className="mt-4 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-center text-sm text-neutral-500">
          No teacher timecards for this center yet.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-neutral-100 rounded-xl border border-neutral-100">
          {rows.map((row) => {
            const aboveThreshold = thresholdConfigured && (row.summary?.overtimeMinutes || 0) > 0;
            return (
              <li
                key={row.profileId}
                data-testid={`admin-timecard-review-row-${row.profileId}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3"
              >
                <div className="min-w-[170px] flex-1">
                  <p className="text-sm font-semibold text-neutral-900">{row.fullName}</p>
                  <p className="text-xs text-neutral-500">{formatDuration(row.summary?.workedMinutes || 0)} actual time</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${timecardStatusTone(row.timecard?.status)}`}>
                  {timecardStatusLabel(row.timecard?.status)}
                </span>
                {aboveThreshold && (
                  <span className="rounded-full bg-[#C6851E]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A5A12]">
                    {formatDuration(row.summary.overtimeMinutes)} above threshold
                  </span>
                )}
                {(row.summary?.correctionCount || 0) > 0 && (
                  <span className="text-xs text-neutral-500">
                    {row.summary.correctionCount} correction{row.summary.correctionCount === 1 ? "" : "s"}
                  </span>
                )}
                <button
                  type="button"
                  data-testid={`admin-timecard-review-open-${row.profileId}`}
                  onClick={() => onReview(row)}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-hover"
                >
                  <PaperPlaneTilt size={13} weight="duotone" />
                  Review
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function TimecardReviewDialog({ profileId, fullName, weekStart, onClose, onChanged }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [approvalNote, setApprovalNote] = useState("");
  const [reopenReason, setReopenReason] = useState("");
  const [correction, setCorrection] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const next = await getStaffTimecardSnapshot({ weekStart, profileId });
        if (mounted) setSnapshot(next);
      } catch (err) {
        if (mounted) setError(err?.message || "Could not load this timecard.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [profileId, weekStart]);

  async function persist(action) {
    setSaving(true);
    setError("");
    try {
      const next = await action();
      setSnapshot(next);
      await onChanged();
      return next;
    } catch (err) {
      const message = err?.message || "Could not update this timecard.";
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (!snapshot?.timecard?.id) return;
    const next = await persist(() => approveStaffTimecard({
      timecardReviewId: snapshot.timecard.id,
      note: approvalNote.trim() || null,
    }));
    if (next) {
      setApprovalNote("");
      toast.success("Timecard approved");
    }
  }

  async function handleReopen() {
    if (!snapshot?.timecard?.id || !reopenReason.trim()) {
      setError("Add a reason before reopening this timecard.");
      return;
    }
    const next = await persist(() => reopenStaffTimecard({
      timecardReviewId: snapshot.timecard.id,
      reason: reopenReason.trim(),
    }));
    if (next) {
      setReopenReason("");
      toast.success("Timecard reopened for review");
    }
  }

  async function handleCorrection(event) {
    event.preventDefault();
    if (!correction) return;
    const clockIn = new Date(correction.clockIn);
    const clockOut = new Date(correction.clockOut);
    if (Number.isNaN(clockIn.getTime()) || Number.isNaN(clockOut.getTime()) || clockOut <= clockIn) {
      setError("Enter a clock-out time after clock-in.");
      return;
    }
    if (!correction.reason.trim()) {
      setError("A correction reason is required.");
      return;
    }

    const next = await persist(() => correctStaffTimesheetEntry({
      timesheetEntryId: correction.entryId,
      clockIn: clockIn.toISOString(),
      clockOut: clockOut.toISOString(),
      breakMinutes: Number(correction.breakMinutes) || 0,
      reason: correction.reason.trim(),
      notes: correction.notes.trim() || null,
    }));
    if (next) {
      setCorrection(null);
      toast.success("Time entry corrected");
    }
  }

  const card = snapshot?.timecard || {};
  const summary = snapshot?.summary || {};
  const status = card.status || "open";
  const canCorrect = status === "open" || status === "reopened";

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DialogContent
        data-testid="admin-timecard-review-dialog"
        className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl border-neutral-200 bg-white p-5 sm:max-w-2xl sm:p-6"
      >
        <DialogHeader className="pr-8">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Timecard review</p>
          <DialogTitle className="font-heading text-xl font-semibold text-neutral-900">{fullName}</DialogTitle>
          <DialogDescription className="sr-only">
            Review, approve, reopen, or correct this staff member's timecard.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="h-7 w-7 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          </div>
        ) : error && !snapshot ? (
          <p role="alert" className="mt-5 rounded-xl bg-[#C6851E]/10 p-3 text-sm text-[#8A5A12]">{error}</p>
        ) : snapshot ? (
          <div className="mt-5 space-y-4">
            {error && <p role="alert" className="rounded-xl bg-[#C6851E]/10 p-3 text-sm text-[#8A5A12]">{error}</p>}

            <section className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Status</p>
                  <p className="mt-1 text-sm font-semibold text-neutral-900">{timecardStatusLabel(status)}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${timecardStatusTone(status)}`}>
                  {timecardStatusLabel(status)}
                </span>
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <TimecardMetric label="Actual time" value={formatDuration(summary.workedMinutes || 0)} />
                <TimecardMetric label="Entries" value={summary.entryCount || 0} />
                <TimecardMetric label="Corrections" value={summary.correctionCount || 0} />
                <TimecardMetric
                  label={snapshot.policy?.status === "configured" ? "Above threshold" : "Threshold"}
                  value={snapshot.policy?.status === "configured"
                    ? formatDuration(summary.overtimeMinutes || 0)
                    : "Not configured"}
                />
              </dl>
              {card.reopenReason && (
                <p className="mt-3 rounded-xl bg-[#C6851E]/10 p-2 text-xs text-[#8A5A12]">
                  <ArrowCounterClockwise className="mr-1 inline" size={14} weight="duotone" />
                  Reopened: {card.reopenReason}
                </p>
              )}
              {card.approvalNote && <p className="mt-3 text-xs text-neutral-600">Approval note: “{card.approvalNote}”</p>}
            </section>

            {status === "submitted" && (
              <section className="rounded-2xl border border-primary/20 bg-primary/5 p-4">
                <p className="text-sm text-neutral-700">Approve this reviewed period, or reopen it with a reason before any correction.</p>
                <label className="mt-3 block">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Approval note <span className="font-normal normal-case tracking-normal">(optional)</span></span>
                  <input
                    data-testid="admin-timecard-approval-note"
                    value={approvalNote}
                    onChange={(event) => setApprovalNote(event.target.value)}
                    placeholder="Visible in this timecard's review history."
                    className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  />
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    data-testid="admin-timecard-approve"
                    disabled={saving}
                    onClick={handleApprove}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
                  >
                    <CheckCircle size={16} weight="duotone" />
                    Approve timecard
                  </button>
                </div>
              </section>
            )}

            {(status === "submitted" || status === "approved") && (
              <section className="rounded-2xl border border-neutral-200 bg-white p-4">
                <label className="block">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Reason to reopen</span>
                  <textarea
                    rows={2}
                    data-testid="admin-timecard-reopen-reason"
                    value={reopenReason}
                    onChange={(event) => setReopenReason(event.target.value)}
                    placeholder="Explain what needs review or correction."
                    className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  />
                </label>
                <button
                  type="button"
                  data-testid="admin-timecard-reopen"
                  disabled={saving || !reopenReason.trim()}
                  onClick={handleReopen}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#C6851E]/30 bg-[#C6851E]/5 px-3.5 py-2 text-sm font-semibold text-[#8A5A12] hover:bg-[#C6851E]/10 disabled:opacity-60"
                >
                  <ArrowCounterClockwise size={16} weight="duotone" />
                  Reopen for review
                </button>
              </section>
            )}

            <section>
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h3 className="font-heading text-base font-semibold text-neutral-900">Actual entries</h3>
                  <p className="mt-1 text-xs text-neutral-500">Corrections require a manager reason and remain in the audit history.</p>
                </div>
                {!canCorrect && (
                  <p className="text-xs text-neutral-500">Reopen this timecard to correct an entry.</p>
                )}
              </div>
              {snapshot.entries.length === 0 ? (
                <p className="mt-3 rounded-xl border border-dashed border-neutral-200 bg-neutral-50 p-4 text-center text-sm text-neutral-500">
                  No actual-time entries in this review week.
                </p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {snapshot.entries.map((entry) => (
                    <li key={entry.id} className="rounded-xl border border-neutral-200 bg-white p-3">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-neutral-900">
                            {new Date(entry.clock_in).toLocaleString()} → {entry.clock_out ? new Date(entry.clock_out).toLocaleString() : "Open"}
                          </p>
                          <p className="mt-1 text-xs text-neutral-500">
                            {formatDuration(entry.worked_minutes || 0)} actual time
                            {entry.break_minutes > 0 ? ` · ${entry.break_minutes}m break` : ""}
                          </p>
                          {entry.notes && <p className="mt-1 text-xs text-neutral-600">“{entry.notes}”</p>}
                        </div>
                        {canCorrect && entry.clock_out && (
                          <button
                            type="button"
                            data-testid={`admin-timecard-correct-${entry.id}`}
                            onClick={() => setCorrection({
                              entryId: entry.id,
                              clockIn: toLocalInput(entry.clock_in),
                              clockOut: toLocalInput(entry.clock_out),
                              breakMinutes: String(entry.break_minutes || 0),
                              reason: "",
                              notes: entry.notes || "",
                            })}
                            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
                          >
                            <PencilSimple size={13} weight="duotone" />
                            Correct
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {correction && (
              <form data-testid="admin-timecard-correction-form" onSubmit={handleCorrection} className="rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-4">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-heading text-base font-semibold text-neutral-900">Correct time entry</h3>
                  <button type="button" onClick={() => setCorrection(null)} className="text-xs font-semibold text-neutral-600 hover:text-neutral-900">Cancel</button>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Clock in</span>
                    <input type="datetime-local" value={correction.clockIn} onChange={(event) => setCorrection({ ...correction, clockIn: event.target.value })} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Clock out</span>
                    <input type="datetime-local" value={correction.clockOut} onChange={(event) => setCorrection({ ...correction, clockOut: event.target.value })} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Break minutes</span>
                    <input type="number" min="0" max="599" value={correction.breakMinutes} onChange={(event) => setCorrection({ ...correction, breakMinutes: event.target.value })} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Correction reason</span>
                    <input value={correction.reason} onChange={(event) => setCorrection({ ...correction, reason: event.target.value })} required className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                  </label>
                </div>
                <label className="mt-3 block">
                  <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Updated note</span>
                  <input value={correction.notes} onChange={(event) => setCorrection({ ...correction, notes: event.target.value })} className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none" />
                </label>
                <button type="submit" disabled={saving} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">
                  <CheckCircle size={16} weight="duotone" />
                  {saving ? "Saving…" : "Save correction"}
                </button>
              </form>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function TimekeepingPolicyDialog({ policy, onClose, onSaved }) {
  const [threshold, setThreshold] = useState(
    policy?.weeklyReviewThresholdMinutes == null ? "" : String(policy.weeklyReviewThresholdMinutes),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    const raw = threshold.trim();
    const nextThreshold = raw === "" ? null : Number(raw);
    if (nextThreshold !== null && (!Number.isInteger(nextThreshold) || nextThreshold < 1 || nextThreshold > 10080)) {
      setError("Use a whole number from 1 to 10,080 minutes, or clear the value.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updateStaffTimekeepingPolicy({ weeklyReviewThresholdMinutes: nextThreshold });
      toast.success(nextThreshold == null ? "Review threshold cleared" : "Review threshold saved");
      await onSaved();
    } catch (err) {
      setError(err?.message || "Could not save the review threshold.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !saving) onClose();
      }}
    >
      <DialogContent className="max-w-md rounded-2xl border-neutral-200 bg-white p-5 sm:max-w-md">
        <DialogHeader className="pr-8">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Center configuration</p>
          <DialogTitle className="font-heading text-xl font-semibold text-neutral-900">Review threshold</DialogTitle>
          <DialogDescription className="sr-only">
            Configure a center-owned actual-time review threshold.
          </DialogDescription>
        </DialogHeader>
        <p className="mt-3 text-sm text-neutral-600">
          HoneyAcorn will flag actual time above this center-selected weekly threshold for review. It does not calculate pay or make a legal determination.
        </p>
        <label className="mt-4 block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">Minutes per Monday-start week</span>
          <input data-testid="admin-timecard-policy-threshold" type="number" min="1" max="10080" value={threshold} onChange={(event) => setThreshold(event.target.value)} placeholder="Leave blank for not configured" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none" />
        </label>
        {error && <p role="alert" className="mt-2 text-sm text-[#8A5A12]">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="rounded-full border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">Cancel</button>
          <button data-testid="admin-timecard-policy-save" type="button" onClick={save} disabled={saving} className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60">{saving ? "Saving…" : "Save threshold"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TimecardMetric({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-[0.12em] text-neutral-500">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-neutral-900">{value}</dd>
    </div>
  );
}

function timecardStatusTone(status) {
  switch (status) {
    case "submitted":
      return "bg-primary/15 text-primary-hover";
    case "approved":
      return "bg-primary/15 text-primary-hover";
    case "reopened":
      return "bg-[#C6851E]/15 text-[#8A5A12]";
    default:
      return "bg-neutral-100 text-neutral-600";
  }
}

function ShiftEditor({ shift, profileId, day, staff, classrooms, organizationId, createdBy, onClose, onSaved }) {
  const isNew = !shift;
  const defaultStart = shift ? new Date(shift.starts_at) : withTime(day, 9);
  const defaultEnd = shift ? new Date(shift.ends_at) : withTime(day, 17);

  const [form, setForm] = useState({
    profile_id: shift?.profile_id || profileId,
    classroom_id: shift?.classroom_id || (classrooms[0]?.id || ""),
    starts_at: toLocalInput(defaultStart.toISOString()),
    ends_at: toLocalInput(defaultEnd.toISOString()),
    role_label: shift?.role_label || "",
    notes: shift?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.profile_id || !form.classroom_id || !form.starts_at || !form.ends_at) {
      toast.error("Staff, classroom, and times are required.");
      return;
    }
    const payload = {
      profile_id: form.profile_id,
      classroom_id: form.classroom_id,
      starts_at: new Date(form.starts_at).toISOString(),
      ends_at: new Date(form.ends_at).toISOString(),
      role_label: form.role_label.trim() || null,
      notes: form.notes.trim() || null,
    };
    if (new Date(payload.ends_at) <= new Date(payload.starts_at)) {
      toast.error("End time must be after start time.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await createShift({ ...payload, organization_id: organizationId, created_by: createdBy });
        toast.success("Shift created");
      } else {
        await updateShift(shift.id, payload);
        toast.success("Shift updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="admin-scheduling-shift-editor"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-neutral-900">
            {isNew ? "New shift" : "Edit shift"}
          </h2>
          <button
            type="button"
            data-testid="admin-scheduling-shift-editor-close"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="space-y-3">
          <Field label="Staff" required>
            <select
              data-testid="admin-scheduling-shift-editor-staff"
              value={form.profile_id}
              onChange={(e) => setForm({ ...form, profile_id: e.target.value })}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
            >
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.full_name}</option>
              ))}
            </select>
          </Field>
          <Field label="Classroom" required>
            <select
              data-testid="admin-scheduling-shift-editor-classroom"
              value={form.classroom_id}
              onChange={(e) => setForm({ ...form, classroom_id: e.target.value })}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none"
            >
              {classrooms.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start" required>
              <input
                type="datetime-local"
                data-testid="admin-scheduling-shift-editor-starts-at"
                value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </Field>
            <Field label="End" required>
              <input
                type="datetime-local"
                data-testid="admin-scheduling-shift-editor-ends-at"
                value={form.ends_at}
                onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </Field>
          </div>
          <Field label="Role label">
            <input
              data-testid="admin-scheduling-shift-editor-role-label"
              value={form.role_label}
              onChange={(e) => setForm({ ...form, role_label: e.target.value })}
              placeholder="lead, float, sub…"
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </Field>
          <Field label="Notes">
            <textarea
              rows={2}
              data-testid="admin-scheduling-shift-editor-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
          </Field>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-scheduling-shift-editor-save"
            disabled={saving}
            onClick={save}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-[0.12em] text-neutral-500">
        {label}
        {required && <span className="text-[#C6851E]"> *</span>}
      </span>
      {children}
    </label>
  );
}

function withTime(date, hour) {
  const d = new Date(date);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// Live ratio awareness strip — one row per classroom × 7 day cells, each
// showing "children / staff" with a state-coloured dot. Subtle enough that
// the schedule below remains the focus; compact enough to scan at a glance.
// ---------------------------------------------------------------------------

function ClassroomRatioStrip({ ratios, days }) {
  if (!ratios || ratios.length === 0) return null;

  // Group by classroom.
  const byClassroom = new Map();
  for (const r of ratios) {
    if (!byClassroom.has(r.classroom_id)) {
      byClassroom.set(r.classroom_id, { name: r.classroom_name, required: r.required_staff_ratio, days: {} });
    }
    byClassroom.get(r.classroom_id).days[r.day] = r;
  }

  const isoDay = (d) => d.toISOString().slice(0, 10);

  // Tone tokens per state (kept pastel to avoid shouting).
  const toneFor = (state) => {
    switch (state) {
      case "over":
        return { dot: "bg-[#C6851E]", text: "text-[#8A5A12]", bg: "bg-[#C6851E]/8", label: "Under ratio" };
      case "near":
        return { dot: "bg-amber-400", text: "text-amber-700", bg: "bg-amber-50", label: "Near limit" };
      case "healthy":
        return { dot: "bg-primary", text: "text-primary-hover", bg: "bg-primary/5", label: "Healthy" };
      default:
        return { dot: "bg-neutral-300", text: "text-neutral-500", bg: "bg-neutral-50", label: "No ratio set" };
    }
  };

  return (
    <section
      data-testid="admin-scheduling-ratio-strip"
      className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white"
    >
      <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-2.5">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          Classroom ratios
        </p>
        <p className="hidden text-[11px] text-neutral-500 sm:block">
          Children on roll / staff on the clock · based on today's attendance and timesheets.
        </p>
      </div>
      <table className="min-w-[780px] w-full">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white p-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-neutral-400">
              Classroom
            </th>
            {days.map((d) => (
              <th
                key={d.toISOString()}
                className="p-2.5 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-400"
              >
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from(byClassroom.entries()).map(([cid, room]) => (
            <tr key={cid} className="border-t border-neutral-100">
              <td className="sticky left-0 z-10 bg-white p-2.5">
                <p
                  data-testid={`admin-scheduling-ratio-classroom-${cid}`}
                  className="truncate font-heading text-xs font-semibold text-neutral-900"
                >
                  {room.name}
                </p>
                {room.required ? (
                  <p className="text-[10px] text-neutral-500">
                    1 : {room.required} required
                  </p>
                ) : (
                  <p className="text-[10px] text-neutral-400">No ratio set</p>
                )}
              </td>
              {days.map((d) => {
                const cell = room.days[isoDay(d)];
                const state = cell?.ratio_state || "none";
                const tone = toneFor(state);
                const label = state === "none"
                  ? "No ratio"
                  : `${cell?.children_present ?? 0} children · ${cell?.staff_on_clock ?? 0} staff · ${tone.label}`;
                return (
                  <td key={d.toISOString()} className="p-1.5 text-center align-middle">
                    <span
                      data-testid={`admin-scheduling-ratio-${cid}-${isoDay(d)}`}
                      data-state={state}
                      title={label}
                      aria-label={label}
                      className={`inline-flex items-center gap-1 rounded-full border border-transparent px-2 py-0.5 text-[10px] font-semibold ${tone.bg} ${tone.text}`}
                    >
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${tone.dot}`} />
                      {cell?.children_present ?? 0}
                      <span className="text-neutral-400">/</span>
                      {cell?.staff_on_clock ?? 0}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
